import { Subject, combineLatest, merge } from 'rxjs'
import { startWith, map, debounceTime, distinctUntilChanged, share } from 'rxjs/operators'
import { Scroll, Resize } from 'libraries/front-end-core/services/WindowService'
import { assign, isEqual, last } from 'libraries/lodash'
import { getHistoryState } from 'libraries/front-end-core/core/history'

const HEIGHT_ADJUSTMENT = 22

const Breakpoints = new Subject()
const ScrollUpdate = new Subject()
export const updateBreakpoints = function () {
  Breakpoints.next(...arguments)
}
export const updateScroll = function () {
  ScrollUpdate.next(...arguments)
}

const mapStickyElement = function (el) {
  const { top, height, left, width, right } = el.getBoundingClientRect()
  return assign({ top, height, left, width, right }, el.dataset)
}

const getBreakpoints = function (empty = false) {
  const container = document.querySelector('.scrolling-container')
  if (!container || empty) {
    return []
  }

  const { top, left: cLeft, right: cRight } = container.getBoundingClientRect()
  const { scrollTop } = container
  const { maxWidth } = window.getComputedStyle(container.children[0])
  return [...container.querySelectorAll('.sticky')]
    .map(mapStickyElement)
    .map(item => ({
      ...item,
      top: item.top - top + scrollTop,
      left: (item.left + item.right - cRight - cLeft) / 2,
      depth: +item.depth,
      maxWidth
    }))
}

const stackHeight = function (stack) {
  return stack.reduce((a, i) => a + i.height, 0) - HEIGHT_ADJUSTMENT * Math.max(stack.length + 1 / 2, 0)
}

const findMax = function (breakpoints, lowerLimit, upperLimit, depth, lastIndex = 0) {
  let result
  let current
  let next
  let previous
  let fix = null
  let nextIndex = 0
  for (let i = lastIndex; i < breakpoints.length; i++) {
    previous = breakpoints[i - 1]
    current = breakpoints[i]
    next = breakpoints[i + 1]
    if (
      current.depth === depth &&
      current.top >= lowerLimit &&
      current.top < upperLimit
    ) {
      if (!(next && next.depth <= current.depth && (
        next.top === current.top + current.height || upperLimit > next.top - current.height
      ))) {
        result = current
        nextIndex = i
      } else {
        result = null
        nextIndex = i
      }
    } else if (previous && previous.depth === depth && current.top - previous.height <= upperLimit && upperLimit < current.top && previous.top + previous.height < current.top) {
      fix = previous.id
    }
    if (current.top >= upperLimit) {
      break
    }
  }

  if (result && current.depth <= result.depth && upperLimit + result.height > current.top) {
    return { result: null, fix: result.id }
  }

  return { result, fix, lastIndex: nextIndex }
}

const computeStack = function (breakpoints, scrollTop) {
  if (breakpoints.length === 0) {
    return []
  }
  const stack = []
  let current = {}
  while ((current = findMax(breakpoints, (stack.length && last(stack).top) || 0, stackHeight(stack) + scrollTop, stack.length + 1, current.lastIndex)).result) {
    stack.push(current.result)
  }
  FixedHelper.next(current.fix)

  return stack
}

const computeNavigation = function (breakpoints, scrollTop) {
  if (breakpoints.length === 0) {
    return []
  }
  const stack = []
  let current = null
  do {
    current = null
    const sh = stackHeight(stack.map(i => breakpoints[i]))
    for (let i = last(stack) || 0; i < breakpoints.length; i++) {
      const bp = breakpoints[i]
      if (bp.top > scrollTop + sh + HEIGHT_ADJUSTMENT / 2 + 2) {
        break
      }
      if (bp.depth === stack.length + 1) {
        current = i
      }
    }

    if (current !== null) {
      stack.push(current)
    }
  } while (current !== null)

  return stack.map(i => breakpoints[i])
}

const stackComparer = function (stack) {
  return stack.reduce((a, s) => a + '.' + (s.priorityId || s.id) + s.selected + s.left, '')
}

const combineBreakpoints = (combinator) => combineLatest(
  [merge(
    Resize.pipe(map(x => false)),
    Breakpoints
  ).pipe(
    startWith(true),
    debounceTime(5),
    map(getBreakpoints)
  ),
  merge(
    Scroll.pipe(map(x => x.scrollMain), startWith(getHistoryState().scrollMain || 0)),
    ScrollUpdate
  )],
  combinator
).pipe(distinctUntilChanged(isEqual, stackComparer))

export const AccordionHeader = combineBreakpoints(computeStack).pipe(map(stack => stack.filter(i => +i.expanded)))

const FixedHelper = new Subject()
export const AccordionFixedHelper = FixedHelper.pipe(distinctUntilChanged(), share())
export const AccordionNavigatorHelper = combineBreakpoints(computeNavigation).pipe(map(ns => ns.map(n => n.nodeId)), share())
